import React, { useState, useEffect, memo, useContext } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { FieldArray, change, arrayPush, arrayRemove } from "redux-form";

import { Button } from "components";
import { ApplicationEntryProgressContext } from "components/contexts";
import MerchantSensitiveDataContext from "components/contexts/merchantSensitiveDataContext";
import { InitialGuidId } from "globalConstants";
import { ownerPrincipalValidationInfoActions } from "store";

import Owners from "./owners";
import Signer from "./signer";

import styles from "../formSections.module.scss";

const OwnerPrincipalSection = ({
	formName,
	sectionName = "ownerPrincipalSection",
	change,
	arrayPush,
	arrayRemove,
	owners,
	signer,
	ownersValidationInfo,
	signerValidationInfo,
	removeOwnerValidationInfo,
	removeSignerValidationInfo,
	allLoading,
	isTinOptional = false,
	hiddenFieldsOnMPA,
	isPaperApplication,
	isMerchantForm,
	isMpaForm,
}) => {
	const { isReadOnly } = useContext(MerchantSensitiveDataContext);
	const { triggerSave } = useContext(ApplicationEntryProgressContext);
	const ownersOrDefault = owners || [];
	const signerOrDefault = signer || {};

	const addOwnersDisabled = ownersOrDefault.length >= 4;

	const [disabled, setDisabled] = useState(signerOrDefault.id ? true : false);

	useEffect(() => {
		if (signer) {
			if (_.isEmpty(signer) || signer?.id === null) {
				return setDisabled(false);
			} else {
				return setDisabled(true);
			}
		}
	}, [signer, setDisabled]);

	const addSigner = () => {
		const emptySigner = {
			id: InitialGuidId,
			isAuthorizedSigner: true,
		};

		change(formName, `${sectionName}.signer`, emptySigner);
		setDisabled(true);
		triggerSave?.();
	};

	const removeSigner = () => {
		removeSignerValidationInfo();
		change(formName, `${sectionName}.signer`, { id: null });
		setDisabled(false);
		triggerSave?.();
	};

	const addOwner = () => {
		arrayPush(formName, `${sectionName}.owners`, {});
		triggerSave?.();
	};

	const removeOwner = index => {
		removeOwnerValidationInfo(index);
		arrayRemove(formName, `${sectionName}.owners`, index);
		triggerSave?.();
	};

	const signerExists = signer?.id;

	const isOwnerBadgeShown =
		!signerExists && !(isPaperApplication || isMerchantForm || isMpaForm);

	const isSignerBadgeShown =
		signerExists && !(isPaperApplication || isMerchantForm || isMpaForm);

	return (
		<>
			<FieldArray
				component={Owners}
				props={{
					formName,
					sectionName,
					change,
					ownersValidationInfo,
					removeOwner,
					allLoading,
					isTinOptional,
					hiddenFieldsOnMPA,
					isPaperApplication,
					isBadgeShown: isOwnerBadgeShown,
				}}
				name="owners"
			/>
			{signerExists && (
				<>
					<hr className={styles.divider} />
					<Signer
						formName={formName}
						sectionName={sectionName}
						change={change}
						signer={signerOrDefault}
						removeSigner={removeSigner}
						signerValidationInfo={signerValidationInfo}
						isTinOptional={isTinOptional}
						isBadgeShown={isSignerBadgeShown}
					/>
				</>
			)}

			<div className={styles.wrappNewOwner}>
				<Button
					value="Add new Owner"
					disabled={addOwnersDisabled || allLoading || isReadOnly}
					onClick={addOwner}
					variant="secondary"
				/>
				<hr className={styles.vl} />
				{!hiddenFieldsOnMPA?.includes("addAuthorizedSignerButton") && (
					<Button
						value="Add Authorized Signer"
						disabled={disabled || allLoading || isReadOnly}
						onClick={addSigner}
						variant="secondary"
					/>
				)}
			</div>
		</>
	);
};

const mapDispatchToProps = {
	removeOwnerValidationInfo:
		ownerPrincipalValidationInfoActions.removeOwnerValidationInfo,
	removeSignerValidationInfo:
		ownerPrincipalValidationInfoActions.removeSignerValidationInfo,
	change,
	arrayPush,
	arrayRemove,
};

export default memo(
	connect(
		state => ({
			ownersValidationInfo: state.ownerPrincipalValidationInfo.owners,
			signerValidationInfo: state.ownerPrincipalValidationInfo.signer,
		}),
		mapDispatchToProps,
	)(OwnerPrincipalSection),
);
